import { writable, get, derived } from 'svelte/store';
import { navigate as goto } from 'svelte-routing';

const browser = typeof window !== 'undefined';
import { selectedStudy, selectedSite, selectedParticipant } from './selectionStore';
import { logout as auth0Logout } from '../lib/auth.js';

export const user = writable(null);
export const isAuthenticated = writable(false);
export const userRole = writable('');

// Create a derived store for user roles that combines roles from both possible locations
export const userRoles = derived(user, $user => {
  if (!$user) return [];
  
  const customDataRoles = $user.customData?.roles || [];
  const directRoles = $user.roles || [];
  
  // Combine and deduplicate roles from both sources
  return [...new Set([...customDataRoles, ...directRoles])];
});

export const userReadOnly = derived(user, $u => $u?.readOnly === true);
export const userBlinded = derived(user, $u => $u?.blinded === true);

export function isReadOnly() {
  return get(userReadOnly);
}
export function isBlinded() {
  return get(userBlinded);
}

// Function to check if user has any of the required roles
export function hasRole(requiredRoles) {
  const currentUserRoles = get(userRoles);
  
  // If requiredRoles is an array, check if the user has at least one of those roles
  if (Array.isArray(requiredRoles)) {
    return requiredRoles.some(role => currentUserRoles.includes(role));
  }
  
  // If requiredRoles is a single string, check if the user has that role
  return currentUserRoles.includes(requiredRoles);
}

// Add a timestamp to track when user data was last refreshed
export const lastRefreshed = writable(0);

// Function to fetch fresh user data from the API
export async function refreshUserData() {
  if (!browser) return null;
  
  try {
    // console.log('Fetching fresh user data from API');
    const response = await fetch('/api/v1/auth/profile');
    
    if (!response.ok) {
      console.error('Failed to fetch user profile:', response.status);
      return null;
    }
    
    const userProfile = await response.json();
    // console.log('Fetched fresh user profile from API');
    
    // Roles are supplied by the backend from the User document, not Auth0 claims.
    const roles = userProfile.roles || ['site'];
    
    // Format the profile data to match our expected format
    const userData = {
      id: userProfile.sub,
      email: userProfile.email,
      firstName: userProfile.given_name || userProfile.name?.split(' ')[0] || '',
      lastName: userProfile.family_name || userProfile.name?.split(' ').slice(1).join(' ') || '',
      picture: userProfile.picture,
      customData: {
        roles: roles,
        organizations: userProfile.organizations || [{
          organizationId: 'default',
          role: roles[0] || 'site',
          status: 'active'
        }],
        primaryOrganizationId: userProfile.primaryOrganizationId || 'default'
      },
      sessionId: userProfile.sid || userProfile.sub,
      
      // Include study and site assignments
      studyIds: userProfile.studyIds || [],
      siteIds: userProfile.siteIds || [],
      
      // Include settings fields
      theme: userProfile.theme || 'dark',
      showBreadcrumbs: userProfile.showBreadcrumbs !== undefined ? userProfile.showBreadcrumbs : true,
      glucoseUnit: userProfile.glucoseUnit || 'mg/dL',
      language: userProfile.language || 'en_US',
      sidebarCollapsed: userProfile.sidebarCollapsed !== undefined ? userProfile.sidebarCollapsed : false,
      
      // Include tetris high score
      tetrisHighScore: userProfile.tetrisHighScore || 0,

      // User flags
      readOnly: userProfile.readOnly || false,
      blinded: userProfile.blinded || false
    };
    

    
    // Set session with this data, but skip triggering another refresh
    setSession(userData, true);
    
    // Update last refreshed timestamp
    lastRefreshed.set(Date.now());
    
    return userData;
  } catch (error) {
    console.error('Error fetching user data from API:', error);
    return null;
  }
}

export function setSession(userData, skipRefresh = false) {
  // console.log('Setting session with user data:', userData);
  
  // First set the stores
  user.set(userData);
  isAuthenticated.set(!!userData);
  
  // Make sure user has roles
  if (userData && !userData.customData?.roles) {
    userData.customData = userData.customData || {};
    userData.customData.roles = ['site'];
  }
  
  // Make sure we persist to localStorage immediately if in browser
  if (browser && userData) {
    try {
      // Clear the logout flag since user is now authenticated
      localStorage.removeItem('logout_time');
      
      localStorage.setItem('user', JSON.stringify(userData));
      // console.log('User data saved to localStorage');
    } catch (err) {
      console.error('Failed to save user data to localStorage:', err);
    }
  }

  // Add roles to the user object
  if (userData?.customData?.roles) {
    if (browser) {
      try {
        localStorage.setItem('roles', JSON.stringify(userData.customData.roles));
      } catch (err) {
        console.error('Failed to save roles to localStorage:', err);
      }
    }
    // Set the userRole store with the first role (assuming one role per user)
    userRole.set(userData.customData.roles[0]);
  }

  // Trigger refresh from API to get latest settings
  if (browser && userData?.id && !skipRefresh) {
    setTimeout(() => {
      refreshUserData().then(refreshedData => {
        if (refreshedData) {
          localStorage.setItem('lastRefreshed', Date.now().toString());
        }
      });
    }, 100); // Small delay to ensure session is fully set
  }
}

export function clearSession() {
  user.set(null);
  isAuthenticated.set(false);
  
  // Reset selection stores
  selectedStudy.set(null);
  selectedSite.set(null);
  selectedParticipant.set(null);
  
  // Only clear storage if in browser
  if (browser) {
    // Set a flag to prevent auto sign-in after logout
    localStorage.setItem('logout_time', Date.now().toString());
    
    // Clear all localStorage items related to authentication and selections
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    localStorage.removeItem('auth0.is.authenticated');
    localStorage.removeItem('selectedStudy');
    localStorage.removeItem('selectedSite');
    localStorage.removeItem('selectedParticipant');
    
    // Clear any other session-related items
    localStorage.removeItem('lastRefreshed');
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    
    // Clear sessionStorage completely
    sessionStorage.clear();
    
    // More aggressive cookie clearing
    const cookiesToClear = [
      'appSession',
      'auth0.is.authenticated', 
      'auth_verification',
      'auth_nonce',
      'auth_state',
      'id_token',
      'auth_token',
      'auth0_user',
      'user_session',
      'connect.sid',
      'sessionid'
    ];
    
    // Clear cookies with multiple path and domain variations
    cookiesToClear.forEach(cookieName => {
      // Clear for current path
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Clear for root path
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      // Clear for parent domain
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      if (domain !== window.location.hostname) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
      }
    });
    
    // Clear any remaining cookies
    document.cookie.split(";").forEach(function(c) { 
      const cookieName = c.split("=")[0].trim();
      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      }
    });
    
    // console.log('Session and selections cleared completely');
  }
}

export async function logout() {
  // Clear local session data first (this now includes selection stores).
  // This runs before the Auth0 redirect so the SPA stores are clean
  // even if the user immediately re-signs-in via the same tab.
  clearSession();

  if (!browser) return;

  // Auth0 SPA SDK handles the full PKCE logout flow:
  //   1. Clears the SDK's local token cache (localStorage)
  //   2. Redirects browser to Auth0's hosted /v2/logout?returnTo=...
  //   3. Auth0 clears its server-side SSO session
  //   4. Auth0 redirects back to returnTo (window.location.origin by default)
  //   5. AuthGuard sees no session, routes to /login
  //
  // We do NOT navigate to /api/v1/auth/logout — that's a JWT-protected
  // backend endpoint and top-level navigations don't carry the Bearer
  // header, so they 401. The legacy flow assumed express-openid-connect
  // held the session in a server cookie; PKCE has no server cookie.
  //
  // Auth0 dashboard requirement: the SPA application's "Allowed Logout
  // URLs" must include `window.location.origin` (e.g.
  // http://localhost:5173 for local dev, https://your.prod.host for prod).
  try {
    await auth0Logout();
  } catch (error) {
    console.error('Error during Auth0 logout:', error);
    // Last-resort fallback: drop to the login page in-tab so the user
    // isn't stranded if the SDK call fails (e.g. network glitch). The
    // stale Auth0 token will be rejected by the resource server on next
    // request, and the user will be forced back through sign-in.
    window.location.href = '/login';
  }
}

// Function to check if auto sign-in should be allowed
// Returns false if user has logged out (permanent prevention)
export function shouldAllowAutoSignIn() {
  if (!browser) return true;
  
  const logoutTime = localStorage.getItem('logout_time');
  if (logoutTime) {
    console.log('Auto sign-in blocked: User has logged out');
    return false;
  }
  
  return true;
}

// Function to automatically sign out after inactivity
let inactivityTimer;

// Role-based timeout limits
const TIMEOUT_LIMITS = {
  ADMIN_TIMEOUT: 60 * 60 * 1000,    // 60 minutes for admin/support users
  REGULAR_TIMEOUT: 15 * 60 * 1000    // 15 minutes for regular users
};

// Function to get timeout limit based on user role
function getTimeoutLimit() {
  if (!browser) return TIMEOUT_LIMITS.REGULAR_TIMEOUT;
  
  try {
    const currentUser = get(user);
    if (!currentUser?.customData?.roles) {
      return TIMEOUT_LIMITS.REGULAR_TIMEOUT;
    }
    
    const userRoles = currentUser.customData.roles;
    
    // Check if user has admin or support role
    const hasPrivilegedRole = userRoles.some(role => 
      role.toLowerCase() === 'admin' || 
      role.toLowerCase() === 'support' ||
      role.toLowerCase() === 'superadmin'
    );
    
    const timeoutLimit = hasPrivilegedRole ? TIMEOUT_LIMITS.ADMIN_TIMEOUT : TIMEOUT_LIMITS.REGULAR_TIMEOUT;
    
    // console.log(`Inactivity timeout set to ${timeoutLimit / 60000} minutes for user with roles:`, userRoles);
    
    return timeoutLimit;
  } catch (error) {
    console.error('Error determining timeout limit, using regular timeout:', error);
    return TIMEOUT_LIMITS.REGULAR_TIMEOUT;
  }
}

function resetInactivityTimer() {
  if (browser) {
    clearTimeout(inactivityTimer);
    
    const timeoutLimit = getTimeoutLimit();
    
    inactivityTimer = setTimeout(() => {
      // Clear session and selections on timeout
      clearSession();
      // Go through proper Auth0 logout to ensure server-side session is also cleared
      const proxyLogoutUrl = '/api/v1/auth/logout?override_returnTo=' + encodeURIComponent(window.location.origin + '/login'); 
      try {
        console.log('Timeout logout: Navigating to Auth0 logout URL');
        window.location.href = proxyLogoutUrl; 
      } catch (error) {
        console.error('Error during timeout logout:', error);
        // If Auth0 logout fails, redirect to login
        window.location.href = '/login';
      }
    }, timeoutLimit);
  }
}

export function startInactivityTimer() {
  if (browser) {
    resetInactivityTimer();
    document.addEventListener('visibilitychange', resetInactivityTimer, true);
    window.addEventListener('focus', resetInactivityTimer, true);
  }
}

export function stopInactivityTimer() {
  if (browser) {
    clearTimeout(inactivityTimer);
    document.removeEventListener('visibilitychange', resetInactivityTimer, true);
    window.removeEventListener('focus', resetInactivityTimer, true);
  }
}

// Re-export everything from userStore
export * from './userStore';
